<script setup lang="ts" generic="SITE_SECTION_NODE">
import type {
  IVirtualizedTreeNode,
  IVirtualizedTreeNodeModel,
} from "#/ui-kit/components/ui/virtualized-tree/virtualized-tree.types";

import DraggableSiteSectionsTree from "#/ui-kit/components/site-details/sections/draggable-sections-tree/draggable-site-sections-tree.vue";
import { ISiteTreeStructureExpose } from "#/ui-kit/components/site-details/site-tree-structure/site-tree-structure.types";
import { useCommonFrontendIntl } from "#/ui-kit/locales/typed-intl";
import { DATA_TEST_IDS, isDefined } from "@voltalis/frontend-utils";
import { computed, ref, useTemplateRef, watch } from "vue";

import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";

export interface ISiteTreeStructureProps<SITE_SECTION_NODE> {
  siteSectionsTree?: IVirtualizedTreeNode<SITE_SECTION_NODE>;
  isLoading?: boolean;
  siteName?: string;
  hideRootNode?: boolean;
  draggable?: boolean;
  autoScrollToNode?: (node: IVirtualizedTreeNodeModel<SITE_SECTION_NODE>) => boolean;
  canDropNode?: (
    draggedNode: IVirtualizedTreeNodeModel<SITE_SECTION_NODE>,
    targetDropNode: IVirtualizedTreeNodeModel<SITE_SECTION_NODE>,
  ) => boolean;
}

const props = defineProps<ISiteTreeStructureProps<SITE_SECTION_NODE>>();
const emits = defineEmits<{
  (
    e: "dropped",
    draggedNode: IVirtualizedTreeNodeModel<SITE_SECTION_NODE>,
    targetDropNode: IVirtualizedTreeNodeModel<SITE_SECTION_NODE>,
  ): void;
}>();
const slots = defineSlots<{
  header?: (props: { searchQuery: string; updateSearchQuery: (value: string) => void }) => unknown;
  loader: () => unknown;
  sectionName: (props: { section: IVirtualizedTreeNodeModel<SITE_SECTION_NODE> }) => unknown;
  nodeIcon: (props: { section: IVirtualizedTreeNodeModel<SITE_SECTION_NODE> }) => unknown;
}>();

const { t, k } = useCommonFrontendIntl();

const draggableSiteSectionsTree = useTemplateRef("draggableSiteSectionsTree");

const searchQuery = ref<string>("");

function updateSearchQuery(value: string) {
  searchQuery.value = value;
}

function filterSectionByName(query: string, node: IVirtualizedTreeNode<SITE_SECTION_NODE>): boolean {
  const nodeName = `${node.name}`;
  return nodeName.toLowerCase().includes(query.toLowerCase());
}

const filteredNodes = computed<Array<IVirtualizedTreeNode<SITE_SECTION_NODE>>>(() => {
  if (!props.siteSectionsTree) {
    return [];
  }

  if (searchQuery.value.length === 0) {
    return props.siteSectionsTree.children ?? [];
  }

  function buildNode(node: IVirtualizedTreeNode<SITE_SECTION_NODE>): IVirtualizedTreeNode<SITE_SECTION_NODE> | null {
    const isValid = filterSectionByName(searchQuery.value, node);

    if (isValid) {
      return node;
    }

    if (!node.children) {
      return null;
    }

    const filteredChildren = node.children.map((n) => buildNode(n)).filter((node) => isDefined(node));
    if (filteredChildren.length === 0) {
      return null;
    }

    return {
      ...node,
      children: filteredChildren,
    };
  }

  return props.siteSectionsTree.children?.map((n) => buildNode(n)).filter((node) => isDefined(node)) ?? [];
});

const siteStructure = computed<IVirtualizedTreeNode<SITE_SECTION_NODE> | undefined>(() => {
  if (!props.siteSectionsTree) return undefined;

  return {
    ...props.siteSectionsTree,
    children: filteredNodes.value,
  };
});

function handleDropped(
  draggedNode: IVirtualizedTreeNodeModel<SITE_SECTION_NODE>,
  targetDropNode: IVirtualizedTreeNodeModel<SITE_SECTION_NODE>,
) {
  emits("dropped", draggedNode, targetDropNode);
}

async function refreshTree(): Promise<void> {
  const tree = draggableSiteSectionsTree.value;
  if (!tree) return;

  await tree.forceUpdate();

  if (props.autoScrollToNode) tree.scrollToNode(props.autoScrollToNode);
}

watch(
  filteredNodes,
  () => {
    void refreshTree();
  },
  {
    flush: "post",
  },
);

defineExpose<ISiteTreeStructureExpose>({
  refreshTree,
});
</script>

<template>
  <div class="site-tree-structure">
    <slot
      v-if="slots.header"
      name="header"
      :search-query="searchQuery"
      :update-search-query="updateSearchQuery"
    />

    <div
      v-else
      class="flex justify-between items-center"
    >
      <h3 class="font-medium text-gray-700">{{ t(k.commonFrontend["site-tree-structure"].title) }}</h3>

      <IconField class="site-tree-structure-search">
        <InputIcon class="pi pi-search" />
        <InputText
          :model-value="searchQuery"
          class="w-full"
          :placeholder="t(k.commonFrontend['site-tree-structure'].searchSection)"
          @update:model-value="searchQuery = $event ?? ''"
          :pt="{
            root: {
              'data-test-id': DATA_TEST_IDS.components.siteTreeStructure.searchTextInput,
            },
          }"
        />
      </IconField>
    </div>

    <template v-if="props.isLoading">
      <slot name="loader" />
    </template>

    <div
      v-else-if="!filteredNodes.length || !siteStructure"
      class="w-full h-full flex items-center justify-center text-gray-500"
    >
      {{ t(k.commonFrontend.misc.noResult) }}
    </div>

    <template v-else>
      <DraggableSiteSectionsTree
        ref="draggableSiteSectionsTree"
        :sectionsTree="siteStructure"
        :canDropNode="props.canDropNode"
        :draggable="props.draggable"
        :hideRootNode="props.hideRootNode"
        @dropped="handleDropped"
      >
        <template #loader>
          <slot name="loader" />
        </template>
        <template #sectionName="{ section }">
          <slot
            name="sectionName"
            :section="section"
          />
        </template>
        <template #nodeIcon="{ section }">
          <slot
            name="nodeIcon"
            :section="section"
          />
        </template>
      </DraggableSiteSectionsTree>
    </template>
  </div>
</template>

<style scoped lang="scss">
.site-tree-structure {
  @apply flex flex-col gap-4 p-6 rounded-2xl w-full h-full;
  background-color: #ffffff80;
}

.site-tree-structure-search {
  width: 400px;
}
</style>
